import React from "react";
import clsx from "clsx";
import { Mail } from "lucide-react";
import EmailRecipientsInput from "./EmailRecipientsInput";

/**
 * The owner's email address, as the GreenRoad profile reports it on login. Null when the
 * profile has none — "Send to my email" is then offered as disabled with the reason.
 */
export const ownerEmailOf = (userData) => {
  const email = userData?.email?.trim();
  if (!email || isPlaceholderEmail(email)) return null;
  return email;
};

// GreenRoad accounts without an address carry "NoEmail@Noemail.com"; sending there only bounces.
export const isPlaceholderEmail = (email) => /@noemail\.com$/i.test((email ?? "").trim());

/** The settings object the API speaks, built from this component's three values. */
export const emailSettingsFor = (sendToMe, ownerEmail, extraRecipients) => ({
  sendToMe: !!sendToMe && !!ownerEmail,
  ownerEmail: ownerEmail ?? null,
  extraRecipients: extraRecipients ?? [],
});

/** One-line description for a confirmation step: who the answer will be emailed to. */
export const describeRecipients = (sendToMe, ownerEmail, extraRecipients) => {
  const names = [];
  if (sendToMe && ownerEmail) names.push(ownerEmail);
  names.push(...(extraRecipients ?? []));
  if (!names.length) return null;
  if (names.length <= 3) return names.join(", ");
  return `${names.slice(0, 2).join(", ")} and ${names.length - 2} more`;
};

/**
 * "Send to email": the switch for the owner's own address plus the list of further recipients.
 * Shared by the scheduling popup and the Send to email screen of an existing scheduled prompt,
 * so the two never drift apart.
 */
const EmailDeliveryOptions = ({
  ownerEmail,
  sendToMe,
  onSendToMeChange,
  extraRecipients,
  onExtraRecipientsChange,
  max,
  compact = false,
}) => {
  const canSendToMe = !!ownerEmail;
  // Said out loud under the fields: who gets the answer right now. Without it, a greyed-out
  // "Send to my email" reads as "no email at all", even once addresses were added below.
  const recipients = describeRecipients(sendToMe, ownerEmail, extraRecipients);

  return (
    <div className={clsx("flex flex-col", compact ? "gap-3" : "gap-4")}>
      <label
        className={clsx(
          "flex items-start gap-3 rounded-base border px-4 py-3 transition-colors",
          canSendToMe
            ? "cursor-pointer border-violet-200 hover:bg-whitesmoke-600"
            : "cursor-not-allowed border-whitesmoke-100 bg-whitesmoke-100/60"
        )}
      >
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 accent-dark-blue-100"
          checked={canSendToMe && !!sendToMe}
          disabled={!canSendToMe}
          onChange={(e) => onSendToMeChange(e.target.checked)}
          data-testid="send-to-me"
        />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex items-center gap-2 text-[14px] font-semibold text-dark-blue-100">
            <Mail size={16} className="shrink-0 text-blue-100" aria-hidden="true" />
            Send to my email
          </span>
          <span className="text-[13px] text-dimgray-200 [overflow-wrap:anywhere]">
            {canSendToMe
              ? `Each answer is emailed to ${ownerEmail}, with a PDF copy attached.`
              : "Your GreenRoad profile has no email address, so the answer can't be sent to you. Add the addresses to email below instead."}
          </span>
        </span>
      </label>

      <div className="flex flex-col gap-1.5">
        <p className="text-[14px] font-semibold text-dark-blue-100">
          {canSendToMe ? "Also send to" : "Send to"}
          {canSendToMe && <span className="ml-1 font-normal text-dimgray-200">(optional)</span>}
        </p>
        <EmailRecipientsInput
          value={extraRecipients}
          onChange={onExtraRecipientsChange}
          ownerEmail={ownerEmail}
          max={max}
        />
      </div>

      <p
        className={clsx(
          "rounded-base px-4 py-2.5 text-[13px] [overflow-wrap:anywhere]",
          recipients ? "bg-blue-100/10 text-dark-blue-100" : "bg-whitesmoke-600 text-dimgray-200"
        )}
        role="status"
        data-testid="email-recipients-summary"
      >
        {recipients
          ? `Each answer will be emailed to ${recipients}, with a PDF copy attached.`
          : canSendToMe
            ? "Nobody will be emailed. You'll still find every answer under Scheduled Tasks."
            : "Nobody will be emailed yet. Add an address above and each answer is emailed there, with a PDF copy attached. You'll still find every answer under Scheduled Tasks."}
      </p>
    </div>
  );
};

export default EmailDeliveryOptions;
